import { Trans, useTranslation } from 'next-i18next';
import { ChartBarFillIcon, MagicwandFillIcon, SparklingFillIcon } from '@/icons';
import { useCountryCode } from '@/shared/hooks';
import { PLAN_FEATURES } from '@/shared/plan/consts';
import { type GroupPricingContent } from '../PricingSection1/groupPricingContent';
import { TableHeader } from './TableHeader';
import { TableSection } from './TableSection';
import { type FeatureSection } from './types';
export const PlanComparisonTable = ({
  groupPricingContent
}: {
  groupPricingContent: GroupPricingContent;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    countryCode,
    isKR
  } = useCountryCode();
  const planFeatures = PLAN_FEATURES[isKR ? 'KR' : 'GLOBAL'];
  const TABLE_DATA: FeatureSection[] = [{
    id: 'PREMIUM_FEATURES',
    title: t('common:pricing.planComparison.premiumFeatures'),
    icon: <SparklingFillIcon size="xl" className="[&>*]:fill-alternative" />,
    features: [{
      id: 'CONCURRENT_USERS',
      label: t('common:pricing.planComparison.concurrentUsers'),
      tooltipText: t('common:pricing.planComparison.concurrentUsersTooltip'),
      data: {
        FREE: {
          isSupported: true,
          text: t('common:pricing.planComparison.maxNum', {
            num: 200
          })
        },
        BASIC: {
          isSupported: true,
          text: t('common:pricing.planComparison.maxNum', {
            num: 200
          })
        },
        PRO: {
          isSupported: true,
          text: t('common:pricing.planComparison.maxNum', {
            num: 200
          })
        }
      }
    }, {
      id: 'PREMIUM_MAPS',
      label: t('common:pricing.planComparison.premiumMaps'),
      tooltipText: t('common:pricing.planComparison.premiumMapsTooltip'),
      data: {
        FREE: {
          isSupported: true,
          text: t('common:pricing.planComparison.someLimited')
        },
        BASIC: {
          isSupported: true,
          text: `+${t('common:pricing.planComparison.basicExclusiveMap')}`
        },
        PRO: {
          isSupported: true,
          text: `+${t('common:pricing.planComparison.allMaps')}`
        }
      }
    }, {
      id: 'PRIVATE_QUIZ',
      label: t('common:pricing.planComparison.privateQuiz'),
      tooltipText: t('common:pricing.planComparison.privateQuizTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('PRIVATE_QUIZ')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('PRIVATE_QUIZ')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('PRIVATE_QUIZ')
        }
      }
    }, {
      id: 'REMOVE_ADS',
      label: t('common:pricing.planComparison.removeAds'),
      tooltipText: t('common:pricing.planComparison.removeAdsTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('REMOVE_ADS')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('REMOVE_ADS')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('REMOVE_ADS')
        }
      },
      visibleCountries: ['ID']
    }, {
      id: 'WORKSHEET',
      label: t('common:pricing.planComparison.worksheet'),
      tooltipText: t('common:pricing.planComparison.worksheetTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('WORKSHEET')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('WORKSHEET')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('WORKSHEET')
        }
      }
    }, {
      id: 'CONTINUE_NEXT_QUIZSET',
      label: t('common:pricing.planComparison.continueNextQuiz'),
      tooltipText: t('common:pricing.planComparison.continueNextQuizTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('CONTINUE_NEXT_QUIZSET')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('CONTINUE_NEXT_QUIZSET')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('CONTINUE_NEXT_QUIZSET')
        }
      }
    }]
  }, {
    id: 'LEARNING_REPORT_FEATURES',
    title: t('common:pricing.planComparison.learningReport'),
    icon: <ChartBarFillIcon size="xl" className="fill-alternative" />,
    features: [{
      id: 'STUDENT_STATS',
      label: t('common:pricing.planComparison.studentStats'),
      tooltipText: t('common:pricing.planComparison.studentStatsTooltip'),
      data: {
        FREE: {
          isSupported: true
        },
        BASIC: {
          isSupported: true
        },
        PRO: {
          isSupported: true
        }
      }
    }, {
      id: 'QUESTION_STATS',
      label: t('common:pricing.planComparison.questionStats'),
      tooltipText: t('common:pricing.planComparison.questionStatsTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('LMS_PARTIAL')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('LMS_PARTIAL')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('LMS_PARTIAL')
        }
      }
    }, {
      id: 'OVERALL_STATS',
      label: t('common:pricing.planComparison.overallStats'),
      tooltipText: t('common:pricing.planComparison.overallStatsTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('LMS_PARTIAL')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('LMS_PARTIAL')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('LMS_PARTIAL')
        }
      }
    }, {
      id: 'AI_TUTOR_CHAT_HISTORY',
      label: t('common:pricing.planComparison.aiTutorChatHistory'),
      tooltipText: t('common:pricing.planComparison.aiTutorChatHistoryTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('LMS_PARTIAL')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('LMS_PARTIAL')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('LMS_PARTIAL')
        }
      },
      visibleCountries: ['KR']
    }]
  }, {
    id: 'QUIZ_AI_FEATURES',
    title: t('common:pricing.planComparison.quizAi'),
    icon: <MagicwandFillIcon size="xl" className="[&>*]:fill-alternative" />,
    features: [{
      id: 'AI_FROM_QUERY',
      label: <Trans t={t} i18nKey={'common:pricing.planComparison.aiFromQuery'} components={{
        Br: <br className="hidden laptop-sm:block" />
      }} />,
      tooltipText: t('common:pricing.planComparison.aiFromQueryTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_FROM_QUERY')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_FROM_QUERY')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_FROM_QUERY')
        }
      }
    }, {
      id: 'AI_FROM_FILE',
      label: <Trans t={t} i18nKey={'common:pricing.planComparison.aiFromFile'} components={{
        Br: <br className="hidden laptop-sm:block" />
      }} />,
      tooltipText: `${t('common:pricing.planComparison.aiFromFileTooltip')} (pdf, csv, txt, docx, pptx, hwp)`,
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_FROM_FILE')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_FROM_FILE')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_FROM_FILE')
        }
      }
    }, {
      id: 'AI_FROM_YOUTUBE',
      label: <Trans t={t} i18nKey={'common:pricing.planComparison.aiFromYoutube'} components={{
        Br: <br className="hidden laptop-sm:block" />
      }} />,
      tooltipText: t('common:pricing.planComparison.aiFromYoutubeTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_FROM_YOUTUBE')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_FROM_YOUTUBE')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_FROM_YOUTUBE')
        }
      }
    }, {
      id: 'AI_SIMILAR_QUIZ_GENERATION',
      label: t('common:pricing.planComparison.aiSimilarQuiz'),
      tooltipText: t('common:pricing.planComparison.aiSimilarQuizTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_SIMILAR_QUIZ_GENERATION')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_SIMILAR_QUIZ_GENERATION')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_SIMILAR_QUIZ_GENERATION')
        }
      }
    }, {
      id: 'AI_CHOICE_GENERATION',
      label: t('common:pricing.planComparison.aiChoiceGeneration'),
      tooltipText: t('common:pricing.planComparison.aiChoiceGenerationTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_CHOICE_GENERATION')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_CHOICE_GENERATION')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_CHOICE_GENERATION')
        }
      }
    }, {
      id: 'AI_QUIZSET_TRANSLATION',
      label: t('common:pricing.planComparison.aiTranslation'),
      tooltipText: t('common:pricing.planComparison.aiTranslationTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_QUIZSET_TRANSLATION')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_QUIZSET_TRANSLATION')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_QUIZSET_TRANSLATION')
        }
      }
    }, {
      id: 'AI_TUTOR',
      label: t('common:pricing.planComparison.aiTutor'),
      tooltipText: t('common:pricing.planComparison.aiTutorTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_TUTOR')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_TUTOR')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_TUTOR')
        }
      },
      visibleCountries: ['KR']
    }]
  }];
  const sections = TABLE_DATA.map(section => ({
    ...section,
    features: section.features.filter(feature => feature.visibleCountries?.includes(countryCode) ?? true)
  }));
  return <div className="px-md tablet-sm:px-lg tablet-lg:px-2xl" data-sentry-component="PlanComparisonTable" data-sentry-source-file="PlanComparisonTable.tsx">
      <div className="min-w-[528px]">
        <TableHeader groupPricingContent={groupPricingContent} data-sentry-element="TableHeader" data-sentry-source-file="PlanComparisonTable.tsx" />
        {sections.map(section => <TableSection key={section.id} section={section} />)}
      </div>
    </div>;
};